import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard,
  FileText,
  Users,
  Award,
  CreditCard,
  Calendar,
  Newspaper,
  FolderOpen,
  UserCheck,
  LogOut,
  Menu,
  X,
  Settings,
  Edit3,
  Crown
} from 'lucide-react';
import { Button } from './ui/button';
import { useState, useEffect } from 'react';

const sidebarLinks = [
  { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
  { name: 'Applications', path: '/admin/applications', icon: FileText },
  { name: 'Members', path: '/admin/members', icon: Users },
  { name: 'Certificates', path: '/admin/certificates', icon: Award },
  { name: 'Payments', path: '/admin/payments', icon: CreditCard },
  { name: 'Events', path: '/admin/events', icon: Calendar },
  { name: 'News', path: '/admin/news', icon: Newspaper },
  { name: 'Documents', path: '/admin/documents', icon: FolderOpen },
  { name: 'Founding Members', path: '/admin/founding-members', icon: UserCheck },
  { type: 'divider', name: 'CMS' },
  { name: 'Page Content', path: '/admin/cms/pages', icon: Edit3 },
  { name: 'Leadership', path: '/admin/cms/leadership', icon: Crown },
  { name: 'Site Settings', path: '/admin/cms/settings', icon: Settings },
];

const AdminLayout = () => {
  const location = useLocation();
  const { user, logout, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Scroll to top and close sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/login" replace />;
  }

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0E285B] transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-new.png" alt="UIEHEC" className="h-10 w-auto" />
            </Link>
            <button className="lg:hidden text-white" onClick={() => setSidebarOpen(false)}>
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {sidebarLinks.map((link, index) => {
              if (link.type === 'divider') {
                return (
                  <div key={index} className="pt-4 pb-2">
                    <span className="px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      {link.name}
                    </span>
                  </div>
                );
              }
              const Icon = link.icon;
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#F5A623]/20 text-[#F5A623]'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                  data-testid={`admin-sidebar-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="h-5 w-5" />
                  {link.name}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-[#F5A623] flex items-center justify-center text-[#0E285B] font-medium">
                {user.full_name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium text-white">{user.full_name}</p>
                <p className="text-xs text-slate-400 capitalize">{user.role}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full justify-start gap-2 border-slate-600 text-slate-300 hover:bg-slate-800"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 lg:ml-64">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="admin-menu-btn">
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-[#0E285B]">Admin Panel</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm">Back to Site</Button>
          </Link>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
